import { supabase } from '../supabase';
import { embedThought } from './openAiService';

// Explicit column list for READS of the `thoughts` table only.
//
// NOT for writes — use mapThoughtToRow below for INSERT/UPDATE payloads.
// (The naming is deliberate: an earlier draft called this THOUGHT_COLUMNS,
// which a future dev could plausibly reach for in an INSERT and silently
// drop `embedding`/`embedded_at`/`body_hash` from the write. READ in the
// name forecloses that path.)
//
// Why this exists: the table also has `embedding vector(3072)`, `embedded_at`,
// and `body_hash` columns (added by migration 20260525000001 + 20260525000002).
// The `embedding` column alone is ~24 KB per row JSON-serialized, so
// `select('*')` on a 1k-row user produces a ~30 MB response — slow at best,
// 500 Internal Server Error at worst when PostgREST or Supabase response
// limits kick in. The home page was hitting exactly that.
//
// This list must stay in lockstep with `mapRowToThought` below — every
// `r.X` access there must have a corresponding column here, and vice versa.
// The bidirectional forcing function lives in thoughtService.test.js:
//   1. THOUGHT_READ_COLUMNS exactly matches an explicit expected SQL column set
//   2. mapRowToThought output keys exactly match an explicit expected key set
// Adding a field on either side without updating both spec lists fails CI.
export const THOUGHT_READ_COLUMNS =
  'id, user_id, body, ts, later, theme, motif, tags, importance, insight, current, intention, comments, in_deck';

// Map a Supabase row to the app's thought object shape
export const mapRowToThought = (r) => ({
  id: r.id,
  userId: r.user_id,
  thought: r.body,
  timestamp: r.ts,
  later: !!r.later,
  theme: r.theme || '',
  motif: r.motif || '',
  tags: r.tags || [],
  // Preserve null so the slider can render "Not set" and the drafts predicate
  // can distinguish "user hasn't picked an importance yet" from a real 0.
  importance: r.importance ?? null,
  insight: !!r.insight,
  current: !!r.current,
  intention: r.intention || '',
  comments: r.comments || '',
  // Flashcard deck membership. true only when explicitly in the deck; NULL
  // (undecided) and false (tossed) both read as not-in-deck for the UI.
  inDeck: r.in_deck === true,
});

// Map the app's thought object to a Supabase row
export const mapThoughtToRow = (t) => ({
  body: t.thought,
  ts: t.timestamp || new Date().toISOString(),
  later: !!t.later,
  theme: t.theme || '',
  motif: t.motif || '',
  tags: t.tags || [],
  importance: t.importance ?? null,
  insight: t.insight ?? true,
  current: !!t.current,
  intention: t.intention || '',
  comments: t.comments || '',
});

// Fetch ALL thoughts for a user, ordered by timestamp descending.
//
// NOTE: this loads every row and is NOT the home-page path anymore — the home
// page uses getDraftThoughts (drafts only) + getThoughtVocabulary (lightweight
// all-rows metadata). Kept for callers that genuinely need the full set:
// useAllThoughts (Timeline/Shuffle/StarField) and the "Your Thoughts" library
// view + its search.
//
// PostgREST caps every response at `max_rows` (1000). A single unpaginated query
// therefore silently returned only the newest 1000 thoughts — so a user with
// more than that lost their oldest ones from every full-corpus view and could
// never find them by search. Page through in 1000-row windows until a short
// page signals the end (mirrors selectAllPages in the Netlify functions).
const THOUGHTS_PAGE_SIZE = 1000;
export const getThoughts = async (userId) => {
  const all = [];
  for (let from = 0; ; from += THOUGHTS_PAGE_SIZE) {
    const { data: rows, error } = await supabase
      .from('thoughts')
      .select(THOUGHT_READ_COLUMNS)
      .eq('user_id', userId)
      .order('ts', { ascending: false })
      .range(from, from + THOUGHTS_PAGE_SIZE - 1);
    if (error) throw error;
    const batch = rows || [];
    all.push(...batch);
    if (batch.length < THOUGHTS_PAGE_SIZE) break; // short/empty page = end
  }
  return all.map(mapRowToThought);
};

// Server-side draft filter, mirroring isDraftThought (src/utils/draftPredicate).
//
// A thought is a draft until it has BOTH a tag AND an importance >= 1. So a
// draft is: tags empty OR importance null/<=0. We push that to the server so
// the home page fetches the (small) set of untriaged thoughts instead of the
// user's entire history.
//
// CONTRACT: this filter must be AT LEAST AS PERMISSIVE as isDraftThought — it
// may return a few non-drafts (the client re-filters with isDraftThought as
// the source of truth), but it must NEVER exclude a real draft. tags is a
// `TEXT[] DEFAULT '{}'`, so empty is '{}' (or NULL on legacy rows).
const DRAFT_FILTER = 'importance.is.null,importance.lte.0,tags.is.null,tags.eq.{}';

export const getDraftThoughts = async (userId) => {
  const { data: rows, error } = await supabase
    .from('thoughts')
    .select(THOUGHT_READ_COLUMNS)
    .eq('user_id', userId)
    .or(DRAFT_FILTER)
    .order('ts', { ascending: false });
  if (error) throw error;
  return (rows || []).map(mapRowToThought);
};

// Lightweight vocabulary fetch: tags/theme/motif across ALL rows, no bodies.
// Feeds extractUniqueValues (themes/motifs/tags autocomplete) without paying
// the cost of the full-body payload. ~3 small columns x N rows.
export const getThoughtVocabulary = async (userId) => {
  const { data: rows, error } = await supabase
    .from('thoughts')
    .select('tags, theme, motif')
    .eq('user_id', userId);
  if (error) throw error;
  return (rows || []).map(r => ({
    tags: r.tags || [],
    theme: r.theme || '',
    motif: r.motif || '',
  }));
};

// Fetch a single thought by id. Used as a fallback by detail/edit/compose
// views: now that the home page only loads drafts, a triaged thought won't be
// in the in-memory array, so opening it by URL must fetch it directly.
// Returns null when no row matches (deleted / wrong id) so callers can render
// a graceful "not found" instead of crashing.
export const getThoughtById = async (thoughtId) => {
  if (!thoughtId) return null;
  const { data, error } = await supabase
    .from('thoughts')
    .select(THOUGHT_READ_COLUMNS)
    .eq('id', thoughtId)
    .maybeSingle();
  if (error) throw error;
  return data ? mapRowToThought(data) : null;
};

// Fetch the thoughts behind an "intention" view (?intention=X). These are
// current, intention-tagged thoughts — typically already triaged, so they are
// NOT in the drafts-only home array and must be queried directly.
//
// ERROR CONTRACT (deliberate divergence): unlike getThoughts / getDraftThoughts
// / getThoughtById / getThoughtVocabulary in this file — which THROW and let the
// caller decide — this function SWALLOWS errors and returns []. It follows the
// list-view convention used by collectionService (getThoughtsByTag,
// getCollectionThoughts): a filtered browse view degrades to "empty" rather
// than surfacing a query error. It also derives the user from the session
// (again mirroring getThoughtsByTag) instead of taking a userId parameter.
export const getThoughtsByIntention = async (intention) => {
  if (!intention) return [];
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return [];
    const { data, error } = await supabase
      .from('thoughts')
      .select(THOUGHT_READ_COLUMNS)
      .eq('user_id', session.user.id)
      .eq('current', true)
      .eq('intention', intention)
      .order('ts', { ascending: false });
    if (error) throw error;
    return (data || []).map(mapRowToThought);
  } catch (error) {
    console.error(`Error fetching thoughts for intention "${intention}":`, error);
    return [];
  }
};

// Create a new thought, returns the new thought's ID.
// Fire-and-forget embedding write after success so the next suggestion sees
// the new thought as a neighbor candidate (KNN tag suggester).
export const createThought = async (userId, thoughtData) => {
  const row = { ...mapThoughtToRow(thoughtData), user_id: userId };
  const { data: inserted, error } = await supabase
    .from('thoughts')
    .insert([row])
    .select('id')
    .single();
  if (error) throw error;
  embedThought(inserted.id, row.body);
  return inserted.id;
};

// Update an existing thought.
// Fire-and-forget re-embed ONLY when the body actually changed — tag-only or
// importance-only edits don't need a fresh embedding and would otherwise burn
// OpenAI calls. Reads the existing body once before the UPDATE to detect the change.
export const updateThought = async (userId, thoughtId, thoughtData) => {
  const row = mapThoughtToRow(thoughtData);

  // Fetch the row's current body so we can detect whether body actually changed.
  // One extra round-trip per save in exchange for skipping unnecessary embeds.
  const { data: existing, error: fetchErr } = await supabase
    .from('thoughts')
    .select('body')
    .eq('id', thoughtId)
    .eq('user_id', userId)
    .maybeSingle();
  if (fetchErr) throw fetchErr;

  const { error } = await supabase
    .from('thoughts')
    .update(row)
    .eq('id', thoughtId)
    .eq('user_id', userId);
  if (error) throw error;

  const bodyChanged = existing && existing.body !== row.body;
  if (bodyChanged && row.body) embedThought(thoughtId, row.body);
};

// Update ONLY a thought's body — used by the flashcard deck's inline editor.
//
// Deliberately NOT updateThought: that maps the payload through mapThoughtToRow,
// which rebuilds every column and defaults `ts` to now(), so a partial edit
// would blank tags/importance/ts/etc. This writes the single `body` column and
// leaves everything else untouched. Derives the user from the session (like
// deckService) so the deck view stays auth-free.
//
// .select('id') so a zero-row update (stale id or RLS-blocked) throws instead of
// silently succeeding — mirrors deckService's tossCard/addToDeck. Fire-and-forget
// re-embed after success (callers only invoke this on an actual body change).
// Note: embedThought itself no-ops for bodies under ~10 chars, so shrinking a
// card below that leaves the prior vector in place — same as updateThought.
export const updateThoughtBody = async (thoughtId, newBody) => {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('thoughts')
    .update({ body: newBody })
    .eq('id', thoughtId)
    .eq('user_id', user.id)
    .select('id');

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error('Update did not change any row (not found or not owned)');
  }

  embedThought(thoughtId, newBody);
};

// Delete a thought
export const deleteThoughtById = async (userId, thoughtId) => {
  const { error } = await supabase
    .from('thoughts')
    .delete()
    .eq('id', thoughtId)
    .eq('user_id', userId);
  if (error) throw error;
};
